// POC (Enhanced Discovery) REST client.
//
// Previously BASE was `process.env.REACT_APP_API_BASE`, which is unset, so every
// request went to the literal URL "undefined/demands/...". Now on the shared client.

import { get, post, put, demandPath, AI_TIMEOUT_MS } from "../../../api/http";

/** GET /demands/{id}/poc -> { setup, notes, due, completed, remindersEnabled, status, outcome } */
export async function getPocSummary(demandId) {
  return get(demandPath(demandId, "/poc"));
}

/** Save the setup form. Checkpoint notes travel with it so they are no longer lost. */
export async function savePocSetup(demandId, setup, notes) {
  return post(demandPath(demandId, "/poc"), { action: "UPSERT_SETUP", setup, notes });
}

export async function listEvidence(demandId, checkpointKey) {
  return get(demandPath(demandId, "/poc/evidence"), { query: { checkpoint: checkpointKey } });
}

/**
 * Presign, PUT straight to S3, and return the metadata to persist.
 *
 * The S3 PUT deliberately does not go through the shared client: it targets AWS,
 * not the AIEA API, and returns no JSON body.
 */
export async function uploadEvidence(demandId, checkpointKey, file) {
  const pre = await post(demandPath(demandId, "/poc/evidence/presign"), {
    filename: file.name,
    type: file.type,
    size: file.size,
    checkpoint: checkpointKey,
  });

  const res = await fetch(pre.url, {
    method: "PUT",
    headers: { "Content-Type": file.type || "application/octet-stream" },
    body: file,
  });
  if (!res.ok) {
    throw new Error(`Upload to S3 failed: ${res.status} ${res.statusText}`);
  }

  return { key: pre.key, name: file.name, url: pre.publicUrl, size: file.size, type: file.type };
}

export async function putEvidenceMetadata(demandId, checkpointKey, fileMeta) {
  return put(demandPath(demandId, "/poc/evidence"), {
    checkpoint: checkpointKey,
    file: fileMeta,
  });
}

/** Record the checkpoint due dates. The server reports whether anything is scheduled. */
export async function createReminderRules(demandId, startDate) {
  return post(demandPath(demandId, "/poc"), { action: "ENABLE_REMINDERS", startDate });
}

/** AI-generated POC report; runs a model server-side, so it gets the long timeout. */
export async function generatePocReport(demandId) {
  return post(demandPath(demandId, "/poc"), { action: "GENERATE_REPORT" }, { timeout: AI_TIMEOUT_MS });
}
